import { AppRegistry } from "react-native";
import AppWithSuspenseBoundary, {
  AppProps,
} from "@indeed/jobsearch-react-native-bundle/viewjob/src/App";
import { setDatadogLoggerImplementation } from "@indeed/jobsearch-react-native-bundle/shared/logging/DatadogLogger.web";
import { enrichContext } from "@indeed/jobsearch-react-native-bundle/shared/logging/DatadogLoggerContext";
import { NavigationType } from "@indeed/jobsearch-react-native-bundle/shared/util/NavigationType";
import withProviderTracking from "@indeed/jobsearch-react-native-bundle/shared/components/ProviderRoot/ProviderRoot";

import { initializeViewjobDatadog } from "./src/datadog/datadogConfig";
import { createAppWithSafeArea } from "./safeArea";
import type { LocaleData } from "@indeed/jobsearch-react-native-bundle/shared/I18n";

// Wire the web Datadog logger so DatadogLogger (e.g. useLogErrorOnce) emits on
// web instead of no-op'ing (RN-5721). The strict-entry codegen rewrites the
// `shared/` package paths above to `shared-strict/`, so each bundle registers
// on its own DatadogLogger.web instance. No-op until the host injects
// globalThis.rnViewjobDatadogConfig.
initializeViewjobDatadog({ setDatadogLoggerImplementation, enrichContext });

// SafeAreaProvider must wrap the app on web standalone so that SafeAreaProviderCompat
// inside NativeStackView (react-native-screens) finds existing insets context and renders
// its container with flex:1. Without it, NativeSafeAreaProvider.web renders a View with
// no style, collapsing the height chain and preventing StickyButtonsRoot's ScrollView
// from getting a bounded height — nothing can scroll. The SafeArea wiring lives in the
// shared `./safeArea` helper (see index.library.tsx / index.server.ts).
const AppWithSafeArea = createAppWithSafeArea<AppProps>(AppWithSuspenseBoundary);

AppRegistry.registerComponent("main", () => withProviderTracking(AppWithSafeArea, "ViewJob"));

const rootTag = document.getElementById("root");
if (process.env.NODE_ENV !== "production") {
  if (!rootTag) {
    throw new Error('Required HTML element with id "root" was not found in the document HTML.');
  }
}

try {
  AppRegistry.runApplication("main", {
    rootTag,
    hydrate: globalThis._initialData?.ssr ?? false,
    initialProps: {
      ...globalThis._rootProps,
      navigationType: NavigationType.PUSH_AS_FIRST,
      preloadedVJData:
        globalThis._rootProps?.preloadedVJData ?? globalThis._initialData?.viewJobClientSideModel,
      // Active-locale gettext catalog inlined by the host (jobseeker-frontend)
      // into _initialData; the client bundle no longer ships the all-locales
      // catalog (RN-6851 / DISCO-5470). Falls back to the bundled catalog when absent.
      catalog: (globalThis._initialData as unknown as { i18nCatalog?: LocaleData } | undefined)
        ?.i18nCatalog,
    } satisfies AppProps,
  });
} catch (e: unknown) {
  // @ts-ignore
  globalThis._latestError = e;
}
